import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { PAYMENT_CONFIG } from '../../config';
import { useAuth } from '../../context/AuthContext';

// Razorpay type declaration
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  popular: boolean;
  razorpayPlanId?: string;
}

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [currentPlan, setCurrentPlan] = useState('free'); // Mock current plan

  const plans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: '/month',
      description: 'Perfect for testing and small projects',
      features: [
        '100 screenshots/month',
        'Basic customization',
        'Standard resolution'
      ],
      popular: false,
      razorpayPlanId: ''
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 1900, // $19 USD = 1900 cents
      period: '/month',
      description: 'Ideal for growing businesses',
      features: [
        '100,000 screenshots/month',
        'Advanced customization',
        'Priority support',
        'Cloud bucket integration',
        'Custom headers'
      ],
      popular: true,
      razorpayPlanId: 'plan_Pro123' // Replace with actual Razorpay plan ID
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 9900, // Price in smallest currency unit
      period: '/month',
      description: 'For large-scale operations',
      features: [
        'Unlimited screenshots',
        'Full customization',
        'Dedicated support',
        'Custom SLA',
        'On-premise deployment',
        'Advanced analytics',
        'Team management',
        'Custom integrations'
      ],
      popular: false,
      razorpayPlanId: 'plan_Enterprise123' // Replace with actual Razorpay plan ID
    }
  ];

  const handleSelectPlan = (plan: Plan) => {
    console.log('🎯 [Plan] Plan selected:', plan.name);
    
    if (plan.id === 'free') {
      console.log('📦 [Plan] Free plan selected - no payment needed');
      setCurrentPlan('free');
      alert('Switched to Free plan successfully!');
      return;
    }

    console.log('💳 [Plan] Paid plan selected - opening dialog');
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    console.log('🚪 [Dialog] Closing payment dialog');
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      // Check if script already loaded
      if (window.Razorpay) {
        console.log('✅ [Script] Razorpay already loaded');
        resolve(true);
        return;
      }

      console.log('📥 [Script] Loading Razorpay script from CDN...');
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        console.log('✅ [Script] Razorpay script loaded successfully');
        resolve(true);
      };
      script.onerror = () => {
        console.error('❌ [Script] Failed to load Razorpay script');
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    console.log('🚀 [Payment] handlePayment called');
    
    if (!selectedPlan) {
      console.error('❌ [Payment] No plan selected');
      return;
    }

    console.log('📦 [Payment] Selected Plan:', {
      id: selectedPlan.id,
      name: selectedPlan.name,
      price: selectedPlan.price
    });

    setLoading(true);
    console.log('⏳ [Payment] Loading state set to true');

    // Load Razorpay script
    console.log('📜 [Payment] Loading Razorpay script...');
    const res = await loadRazorpayScript();

    if (!res) {
      console.error('❌ [Payment] Razorpay SDK failed to load');
      alert('Razorpay SDK failed to load. Please check your internet connection.');
      setLoading(false);
      return;
    }
    console.log('✅ [Payment] Razorpay script loaded successfully');

    try {
      // Create order on backend (This should be an API call to your backend)
      // For now, using mock data
      const orderData = {
        orderId: 'order_' + Math.random().toString(36).substr(2, 9),
        amount: selectedPlan.price.toString(),
        currency: PAYMENT_CONFIG.RAZORPAY.CURRENCY,
        planId: selectedPlan.id,
        planName: selectedPlan.name
      };

      console.log('📋 [Payment] Order Data created:', orderData);

      const options = {
        key: PAYMENT_CONFIG.RAZORPAY.KEY_ID,
        amount: orderData.amount, // Amount in currency subunits (cents)
        currency: orderData.currency,
        name: PAYMENT_CONFIG.RAZORPAY.NAME,
        description: `${selectedPlan.name} Plan Subscription`,
        image: '', // Add your logo URL here if needed
        order_id: orderData.orderId, // Pass the Order ID from backend
        handler: function (response: any) {
          // Payment successful
          console.log('✅ [Payment] Payment Success Handler called');
          console.log('💳 [Payment] Payment Response:', {
            payment_id: response.razorpay_payment_id,
            order_id: response.razorpay_order_id,
            signature: response.razorpay_signature
          });
          
          // Verify payment on backend (This should be an API call)
          // After verification, update the user's plan
          console.log('🔄 [Payment] Updating current plan to:', selectedPlan.id);
          setCurrentPlan(selectedPlan.id);
          setLoading(false);
          handleCloseDialog();
          
          console.log('🧾 [Payment] Navigating to payment summary...');
          // Navigate to payment summary
          navigate('/dashboard/payment-summary', {
            state: {
              payment: {
                id: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature,
                planName: selectedPlan.name,
                amount: selectedPlan.price / 100, // Convert to dollars
                date: new Date().toISOString(),
                status: 'success'
              }
            }
          });
          console.log('✅ [Payment] Navigation complete');
        },
        prefill: {
          name: user?.name || 'User',
          email: user?.email || '',
          contact: '9999999999' // TODO: Add phone number to user profile
        },
        notes: {
          plan_id: selectedPlan.id,
          plan_name: selectedPlan.name
        },
        theme: {
          color: PAYMENT_CONFIG.RAZORPAY.THEME_COLOR
        },
        modal: {
          ondismiss: function() {
            console.log('❌ [Payment] Modal dismissed by user');
            setLoading(false);
          },
          escape: true,
          animation: true,
          confirm_close: true
        }
      };

      console.log('⚙️ [Payment] Razorpay Options created:', {
        key: options.key,
        amount: options.amount,
        currency: options.currency,
        name: options.name,
        order_id: options.order_id,
        prefill: options.prefill
      });

      // Create Razorpay instance and open payment modal
      console.log('🔨 [Payment] Creating Razorpay instance...');
      const razorpayInstance = new window.Razorpay(options);
      console.log('✅ [Payment] Razorpay instance created successfully');
      
      razorpayInstance.on('payment.failed', function (response: any) {
        console.error('❌ [Payment] Payment Failed Handler called');
        console.error('❌ [Payment] Error Details:', response.error);
        alert(`Payment Failed: ${response.error.description}`);
        setLoading(false);
      });

      console.log('🚪 [Payment] Opening Razorpay modal...');
      razorpayInstance.open();
      console.log('✅ [Payment] Razorpay modal opened');
    } catch (error) {
      console.error('❌ [Payment] Exception caught:', error);
      console.error('❌ [Payment] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      alert('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Billing & Plans
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Choose the plan that fits your needs
      </Typography>

      {/* Current Plan Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Current Plan:</strong> {plans.find(p => p.id === currentPlan)?.name}
        </Typography>
      </Alert>

      {/* Plans Grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 3,
        mb: 4
      }}>
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            sx={{ 
              height: '100%',
              position: 'relative',
              border: plan.popular ? '2px solid black' : 'none',
              transform: { xs: 'scale(1)', md: plan.popular ? 'scale(1.05)' : 'scale(1)' },
              transition: 'transform 0.2s',
              '&:hover': { 
                transform: { xs: 'translateY(-4px)', md: plan.popular ? 'scale(1.05)' : 'translateY(-8px)' } 
              }
            }}
          >
            {plan.popular && (
              <Chip 
                label="Most Popular" 
                sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  right: 16,
                  bgcolor: 'black',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                }} 
              />
            )}
            {plan.id === currentPlan && (
              <Chip 
                label="Current Plan" 
                color="success"
                sx={{ 
                  position: 'absolute', 
                  top: 16, 
                  left: 16,
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                }} 
              />
            )}
            <CardContent sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h5" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {plan.name}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3" component="span" fontWeight={800} sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
                  ${plan.price / 100}
                </Typography>
                <Typography variant="body1" component="span" color="text.secondary">
                  {plan.period}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {plan.description}
              </Typography>
              
              <Box sx={{ flexGrow: 1 }}>
                {plan.features.map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CheckIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Button 
                variant={plan.popular ? "contained" : "outlined"}
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                disabled={plan.id === currentPlan}
                onClick={() => handleSelectPlan(plan)}
              >
                {plan.id === currentPlan ? 'Current Plan' : plan.id === 'free' ? 'Downgrade to Free' : 'Upgrade'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Payment Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Plan Upgrade</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" gutterBottom>
                You are about to upgrade to the <strong>{selectedPlan.name}</strong> plan.
              </Typography>
              <Box sx={{ my: 3, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Plan:</Typography>
                  <Typography fontWeight={600}>{selectedPlan.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Amount:</Typography>
                  <Typography fontWeight={600}>${selectedPlan.price / 100}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Billing Cycle:</Typography>
                  <Typography fontWeight={600}>Monthly</Typography>
                </Box>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                You will be redirected to Razorpay for secure payment processing.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handlePayment} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;

